import { Box, Divider, SimpleGrid, Stack, Text } from '@mantine/core';
import dayjs from 'dayjs';
import type { InvoiceDetails } from '@/models/invoices';
import { TOffer } from '@/models/offers';
import { money } from './splitDerive';

const num = (v: number | null | undefined) => (v != null ? Number(v) : 0);

// The rich single-invoice breakdown (issuer / payer / tax / currency conversion).
// Used by the initial invoice's expanded detail in the Related invoices list.
export default function InvoiceDetail({
  invoice,
  offer,
}: {
  invoice: InvoiceDetails;
  offer: TOffer;
}) {
  const baseCur = invoice.baseCurrency || invoice.dueCurrency;
  const dueCur = invoice.dueCurrency;
  const converted = !!baseCur && !!dueCur && baseCur !== dueCur;
  const vatPct = num(invoice.vatRate) * 100;
  const vatLabel = `VAT (${vatPct % 1 ? vatPct.toFixed(1) : vatPct.toFixed(0)}%)`;

  return (
    <Stack gap="md">
      <Divider label="Details" labelPosition="left" />
      <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing="md">
        <Field
          label="Issuing company"
          value={invoice.invoicingCompanyName ?? '—'}
        />
        <Field label="Payer" value={offer.payer?.displayName ?? '—'} />
        <Field label="Payment method" value={invoice.paymentMethod || '—'} />
        <Field
          label="Due date"
          value={
            invoice.dueAt ? dayjs(invoice.dueAt).format('DD.MM.YYYY') : '—'
          }
        />
      </SimpleGrid>

      <Divider label="Amounts" labelPosition="left" />
      <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing="md">
        {invoice.subtotalAmount != null && (
          <Field
            label="Subtotal"
            value={money(num(invoice.subtotalAmount), baseCur)}
          />
        )}
        {invoice.vatAmount != null && (
          <Field
            label={vatLabel}
            value={money(num(invoice.vatAmount), baseCur)}
          />
        )}
        {num(invoice.offRampingFee) > 0 && (
          <Field
            label="Off-ramping fee"
            value={money(num(invoice.offRampingFee), baseCur)}
          />
        )}
        {invoice.totalAmount != null && (
          <Field
            label={`Total (${baseCur})`}
            value={money(num(invoice.totalAmount), baseCur)}
          />
        )}
        <Field label="Paying currency" value={dueCur} />
        {converted && (
          <Field
            label="Conversion"
            value={`1 ${baseCur} = ${num(invoice.dueConversionRate)} ${dueCur}`}
          />
        )}
        <Field
          label={`Amount due (${dueCur})`}
          value={money(num(invoice.dueAmount), dueCur)}
        />
      </SimpleGrid>

      {invoice.vatNote && (
        <Text fz="xs" c="dimmed">
          {invoice.vatNote}
        </Text>
      )}
    </Stack>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Text fz="xs" c="dimmed">
        {label}
      </Text>
      <Text fz="sm" style={{ wordBreak: 'break-word' }}>
        {value}
      </Text>
    </Box>
  );
}
